/**
 * NAZARE Tea Collection — Animation Controller
 * GSAP + ScrollTrigger + Lenis smooth scroll
 */

(function () {
  'use strict';

  // ─── Lenis Smooth Scroll (desktop only) ───────────────────────────────────
  let lenis = null;

  function initLenis() {
    if (window.innerWidth <= 768) return;
    if (typeof Lenis === 'undefined') return;

    lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    lenis.on('scroll', () => {
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.update();
      }
    });

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);
  }

  // ─── Register GSAP plugins ─────────────────────────────────────────────────
  function initGSAP() {
    if (typeof gsap === 'undefined') return;
    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }
  }

  // ─── Reveal animations ────────────────────────────────────────────────────
  // Native IntersectionObserver — does NOT depend on GSAP/ScrollTrigger so the
  // content is ALWAYS shown even if a CDN fails or ScrollTrigger mis-measures.
  // CSS handles the transition via the `.is-revealed` class.
  const REVEAL_SELECTOR = '.reveal, .reveal-left, .reveal-right, .reveal-scale';

  function revealNow(el) {
    el.classList.add('is-revealed');
  }

  function initReveal() {
    const els = document.querySelectorAll(REVEAL_SELECTOR);
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach(revealNow);
      return;
    }

    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealNow(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    els.forEach((el) => {
      // Elements already in view on load reveal immediately.
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        revealNow(el);
      } else {
        io.observe(el);
      }
    });
  }

  // ─── Stagger children ─────────────────────────────────────────────────────
  function initStagger() {
    document.querySelectorAll('[data-stagger]').forEach((container) => {
      const children = container.querySelectorAll('.stagger-child');
      if (!children.length) return;

      children.forEach((child, i) => {
        child.style.transitionDelay = i * 0.1 + 's';
      });

      const reveal = () => children.forEach(revealNow);

      if (!('IntersectionObserver' in window)) {
        reveal();
        return;
      }

      const io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              reveal();
              obs.disconnect();
            }
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      );
      io.observe(container);
    });
  }

  // ─── Failsafe ─────────────────────────────────────────────────────────────
  // If anything ever fails to reveal, force every reveal element visible after a
  // short delay so content is never permanently hidden.
  function initRevealFailsafe() {
    setTimeout(() => {
      document
        .querySelectorAll(REVEAL_SELECTOR + ', .stagger-child')
        .forEach((el) => {
          if (!el.classList.contains('is-revealed')) {
            el.classList.add('is-revealed');
          }
        });
    }, 2500);
  }

  // ─── Hero title word split ────────────────────────────────────────────────
  function initHeroTitle() {
    if (typeof gsap === 'undefined') return;
    const heroTitle = document.querySelector('.hero__title');
    if (!heroTitle) return;

    const text = heroTitle.textContent.trim();
    const words = text.split(/\s+/);
    heroTitle.innerHTML = words
      .map((w) => `<span class="hero__title-word" style="display:inline-block; overflow:hidden; vertical-align:bottom;"><span style="display:inline-block;">${w}</span></span>`)
      .join(' ');

    const innerSpans = heroTitle.querySelectorAll('.hero__title-word span');
    gsap.fromTo(
      innerSpans,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.08,
        delay: 0.2,
      }
    );
  }

  // ─── Draw line SVGs ───────────────────────────────────────────────────────
  function initDrawLines() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    document.querySelectorAll('.draw-line').forEach((line) => {
      const length = line.getTotalLength ? line.getTotalLength() : 500;
      gsap.set(line, {
        strokeDasharray: length,
        strokeDashoffset: length,
      });

      gsap.to(line, {
        strokeDashoffset: 0,
        duration: 1.4,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: line,
          start: 'top 85%',
          once: true,
        },
      });
    });
  }

  // ─── Parallax elements ────────────────────────────────────────────────────
  function initParallax() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    document.querySelectorAll('[data-parallax]').forEach((el) => {
      const speed = parseFloat(el.dataset.parallax) || 0.3;

      gsap.to(el, {
        yPercent: speed * 100 * -1,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    });
  }

  // ─── Clip-path image reveal ───────────────────────────────────────────────
  function initClipReveal() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    document.querySelectorAll('.clip-reveal').forEach((el) => {
      gsap.fromTo(
        el,
        { clipPath: 'inset(0 0 100% 0)' },
        {
          clipPath: 'inset(0 0 0% 0)',
          duration: 1.1,
          ease: 'power3.inOut',
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            once: true,
          },
        }
      );
    });
  }

  // ─── Marquee pause on hidden ──────────────────────────────────────────────
  function initMarqueePause() {
    document.addEventListener('visibilitychange', () => {
      const marquees = document.querySelectorAll('.marquee__inner, [data-marquee]');
      marquees.forEach((el) => {
        el.style.animationPlayState = document.hidden ? 'paused' : 'running';
      });
    });
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  function init() {
    initGSAP();
    initLenis();
    initReveal();
    initStagger();
    initRevealFailsafe();
    initHeroTitle();
    initDrawLines();
    initParallax();
    // initClipReveal removed — clip-reveal is now a pure-CSS effect driven by the
    // `.is-revealed` class (added by the IntersectionObserver in initReveal), so it
    // can never get stuck hidden if ScrollTrigger fails to fire.
    initMarqueePause();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
